import classNames from "classnames";
import { clamp } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useProjectStore } from "../../stores/projectStore";

const noop = () => {};

export function ResizeHandle({
  side,
  editorContainer,
  resizeCenterColumn,
  placement = "edge",
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const elementRef = useRef(null);
  const resizeHandleX = useRef(null);
  const isResizingRef = useRef(false);

  const setColumnWidths = useProjectStore((state) => state.setColumnWidths);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const columnLayoutResizeHooks = useProjectStore((state) => state.columnLayoutResizeHooks);
  const lastMouseX = useRef(null);
  const amountMoved = useRef(0);

  const effectiveContainer = editorContainer ?? columnLayoutResizeHooks?.editorContainer ?? null;
  const effectiveResizeCenterColumn =
    resizeCenterColumn ?? columnLayoutResizeHooks?.resizeCenterColumn ?? noop;

  const isPanelCollapsed =
    side === "left" ? columnWidths.left.collapsed : columnWidths.right.collapsed;

  const handleEventMove = useCallback(
    (event) => {
      if (!isResizingRef.current) return;
      event.preventDefault();
      const clientX = event.type === "touchmove" ? event.touches[0].clientX : event.clientX;
      if (lastMouseX.current === null) lastMouseX.current = clientX;
      let direction;
      if (lastMouseX.current > clientX) {
        direction = "left";
      } else if (lastMouseX.current === clientX) {
        direction = "none";
      } else if (lastMouseX.current < clientX) {
        direction = "right";
      }
      if (direction === "none") return;

      amountMoved.current = clientX - lastMouseX.current;

      setColumnWidths((draft) => {
        const bothCollapsed = draft.left.collapsed && draft.right.collapsed;
        if (isPanelCollapsed && !bothCollapsed) return;

        if (bothCollapsed) {
          const maxMargin = (window.innerWidth - draft.center.min) / 2;
          if (side === "left") {
            draft.left.current = clamp(
              draft.left.current + amountMoved.current,
              draft.left.collapsedMin,
              maxMargin,
            );
          } else {
            draft.right.current = clamp(
              draft.right.current - amountMoved.current,
              draft.right.collapsedMin,
              maxMargin,
            );
          }
          lastMouseX.current = clientX;
          return;
        }

        let newLeft;
        let newCenter;
        let newRight;
        draft.right.min = draft.right.collapsed ? draft.right.collapsedMin : draft.right.openMin;
        draft.left.min = draft.left.collapsed ? draft.left.collapsedMin : draft.left.openMin;
        switch (side) {
          case "left":
            if (draft.left.collapsed) {
              newCenter = clamp(draft.center.current - amountMoved.current, draft.center.min, Infinity);
              const centerDelta = newCenter - draft.center.current;
              if (!draft.right.collapsed) {
                newRight = clamp(draft.right.current - centerDelta, draft.right.min, Infinity);
                if (newRight >= draft.right.min && newCenter >= draft.center.min) {
                  draft.center.current = newCenter;
                  draft.right.current = newRight;
                }
              } else if (newCenter >= draft.center.min) {
                draft.center.current = newCenter;
              }
              break;
            }

            if (
              direction === "right"
              && draft.center.current <= draft.center.min
              && clientX > resizeHandleX.current
            ) {
              return;
            }

            if (
              direction === "left"
              && draft.left.current <= draft.left.min
              && clientX < resizeHandleX.current
            ) {
              return;
            }
            newLeft = clamp(draft.left.current + amountMoved.current, draft.left.min, Infinity);
            newCenter = clamp(draft.center.current - amountMoved.current, draft.center.min, Infinity);

            if (newCenter >= draft.center.min && newLeft >= draft.left.min && direction === "right") {
              draft.left.current = newLeft;
              draft.center.current = newCenter;
            } else if (
              draft.right.current >= draft.right.min
              && newCenter >= draft.center.min
              && direction === "right"
            ) {
              const additional = draft.center.min - newCenter;
              if (draft.right.current - additional - amountMoved.current < draft.right.min) return;
              draft.left.current = newLeft;
              draft.center.current = draft.center.min;
              draft.right.current = clamp(
                draft.right.current - additional - amountMoved.current,
                draft.right.min,
                Infinity,
              );
            } else if (newLeft >= draft.left.min && direction === "left") {
              draft.left.current = clamp(newLeft, draft.left.min, Infinity);
              draft.center.current = clamp(newCenter, draft.center.min, Infinity);
            }
            break;

          case "right":
            if (draft.right.collapsed) {
              newCenter = clamp(draft.center.current + amountMoved.current, draft.center.min, Infinity);
              const centerDelta = newCenter - draft.center.current;
              if (!draft.left.collapsed) {
                newLeft = clamp(draft.left.current - centerDelta, draft.left.min, Infinity);
                if (newLeft >= draft.left.min && newCenter >= draft.center.min) {
                  draft.center.current = newCenter;
                  draft.left.current = newLeft;
                }
              } else if (newCenter >= draft.center.min) {
                draft.center.current = newCenter;
              }
              break;
            }

            if (
              direction === "left"
              && draft.center.current <= draft.center.min
              && clientX > resizeHandleX.current
            ) {
              return;
            }
            if (
              direction === "right"
              && draft.right.current <= draft.right.min
              && clientX > resizeHandleX.current
            ) {
              return;
            }

            newRight = clamp(draft.right.current - amountMoved.current, draft.right.min, Infinity);
            newCenter = clamp(draft.center.current + amountMoved.current, draft.center.min, Infinity);

            if (newCenter > draft.center.min && newRight > draft.right.min && direction === "left") {
              draft.right.current = newRight;
              draft.center.current = newCenter;
            } else if (
              draft.left.current >= draft.left.min
              && newCenter >= draft.center.min
              && direction === "left"
            ) {
              const additional = draft.center.min - newCenter;
              if (draft.left.current + additional + amountMoved.current < draft.left.min) return;
              draft.right.current = newRight;
              draft.center.current = draft.center.min;
              draft.left.current = clamp(
                draft.left.current + additional + amountMoved.current,
                draft.left.min,
                Infinity,
              );
            } else if (newRight >= draft.right.min && direction === "right") {
              draft.right.current = clamp(newRight, draft.right.min, Infinity);
              draft.center.current = clamp(newCenter, draft.center.min, Infinity);
            }
            break;
          default:
            break;
        }

        lastMouseX.current = clientX;
      });

            effectiveResizeCenterColumn();
    },
    [
      effectiveResizeCenterColumn,
      isPanelCollapsed,
      setColumnWidths,
      side,
    ],
  );

  const handleEventUp = useCallback(
    (event) => {
      if (event?.preventDefault) event.preventDefault();
      isResizingRef.current = false;
      setIsResizing(false);
      lastMouseX.current = null;
      if (effectiveContainer) {
        effectiveContainer.style.pointerEvents = "auto";
      }
    },
    [effectiveContainer],
  );

  const handleEventDown = useCallback(
    (event) => {
      const clientX = event.type === "touchstart" ? event.touches[0].clientX : event.clientX;
      event.preventDefault();
      if (effectiveContainer) {
        effectiveContainer.style.pointerEvents = "none";
      }
      isResizingRef.current = true;
      setIsResizing(true);
      resizeHandleX.current = clientX;
      lastMouseX.current = clientX;
      setColumnWidths((draft) => {
        draft.left.initial = draft.left.current;
        draft.center.initial = draft.center.current;
        draft.right.initial = draft.right.current;
      });
    },
    [effectiveContainer, setColumnWidths],
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    document.addEventListener("mousemove", handleEventMove);
    document.addEventListener("mouseup", handleEventUp);
    document.addEventListener("touchmove", handleEventMove, { passive: false });
    document.addEventListener("touchend", handleEventUp);
    document.addEventListener("touchcancel", handleEventUp);

    return () => {
      document.removeEventListener("mousemove", handleEventMove);
      document.removeEventListener("mouseup", handleEventUp);
      document.removeEventListener("touchmove", handleEventMove);
      document.removeEventListener("touchend", handleEventUp);
      document.removeEventListener("touchcancel", handleEventUp);
    };
  }, [handleEventMove, handleEventUp, isResizing]);

  return (
    <div
      ref={elementRef}
      role="separator"
      aria-orientation="vertical"
      aria-label={
        side === "left" ? "Resize left sidebar and editor" : "Resize editor and right sidebar"
      }
      onMouseDown={handleEventDown}
      onTouchStart={handleEventDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={classNames(`resize-handle resize-handle-${side}`, {
        isResizing,
        isHovered,
        isPanelCollapsed,
        "column-gutter-between": placement === "between",
      })}
    />
  );
}
